import { RootState } from '../store'
import { createSelector } from '@reduxjs/toolkit'
import { TeamMember } from './types'

// Team Member Details Selectors
export const selectTeamMembers = (state: RootState) => state.teamMember.teamMembers

export const selectTeamMemberFilters = (state: RootState) => state.teamMember.filters
export const selectSearchQuery = (state: RootState) => state.teamMember.searchQuery

export const selectTeamMemberInfo = (state: RootState) => state.teamMember.teamMemberInfo
export const selectLoadingTeamMemberInfo = (state: RootState) => state.teamMember.loadingTeamMemberInfo

export const selectActiveTeamMembers = createSelector(
  [selectTeamMembers],
  (teamMembers): TeamMember[] => {
    if (!Array.isArray(teamMembers)) return []
    return teamMembers
      .filter((member) => member.status === 'active')
      .sort((a, b) => {
        const aName = `${a.firstName} ${a.middleName || ''} ${a.lastName}`.toLowerCase()
        const bName = `${b.firstName} ${b.middleName || ''} ${b.lastName}`.toLowerCase()
        return aName.localeCompare(bName)
      })
  },
)

// Filtered Team Members Selector
export const selectFilteredTeamMembers = createSelector(
  [selectActiveTeamMembers, selectTeamMemberFilters, selectSearchQuery],
  (teamMembers, filters, searchQuery): TeamMember[] => {
    if (!Array.isArray(teamMembers)) return []
    return teamMembers.filter((member) => {
      const supervisorMatch =
        !filters.supervisor?.length || filters.supervisor.includes(member.supervisor?.name || 'N/A')
      const operationalManagerMatch =
        !filters.operationalManager?.length ||
        filters.operationalManager.includes(member.operationalManager?.name || 'N/A')
      const practiceMatch = !filters.practice?.length || filters.practice.includes(member.practice)
      const functionalAreaMatch =
        !filters.functionalArea?.length || filters.functionalArea.includes(member.functionalArea)
      const jobCodeMatch = !filters.jobCode?.length || filters.jobCode.includes(member.jobCode)
      const accessRoleMatch =
        !filters.accessRole?.length ||
        filters.accessRole.some(
          (role) => role.toLowerCase() === (member.accessRole || 'user').toLowerCase(),
        )
      const isActive = member.status === 'active'

      // Handle search query - if empty, show all results
      let searchMatch = true
      if (searchQuery.trim() !== '') {
        const searchQueryLower = searchQuery.toLowerCase()
        const fullName =
          `${member.firstName} ${member.lastName} ${member.suffix || ''}`.toLowerCase()
        
        searchMatch =
          fullName.includes(searchQueryLower) || 
          member.email.toLowerCase().includes(searchQueryLower) ||
          member.workdayId.toLowerCase().includes(searchQueryLower) ||
          (member.jobCode || '').toLowerCase().includes(searchQueryLower) ||
          (member.supervisor?.name || '').toLowerCase().includes(searchQueryLower) ||
          (member.operationalManager?.name || '').toLowerCase().includes(searchQueryLower) ||
          (member.functionalArea || '').toLowerCase().includes(searchQueryLower) ||
          (member.practice || '').toLowerCase().includes(searchQueryLower) ||
          (member.accessRole === 'admin' ? 'admin' : 'user').includes(searchQueryLower)
      }

      return (
        supervisorMatch &&
        operationalManagerMatch &&
        practiceMatch &&
        functionalAreaMatch &&
        jobCodeMatch &&
        accessRoleMatch &&
        isActive &&
        searchMatch
      )
    })
  },
)

// Filter Options Selector
export const selectFilterOptions = createSelector([selectActiveTeamMembers], (teamMembers) => {
  if (!Array.isArray(teamMembers))
    return {
      'Access Role': ['Admin', 'Manager', 'User'],
      Supervisor: [],
      'Operations Manager': [],
      'Functional Area': [],
      Practice: [],
    }

  const practices = new Set(teamMembers.map((member) => member.practice).filter(Boolean))
  const supervisors = new Set(teamMembers.map((member) => member.supervisor?.name || 'N/A').filter(Boolean))
  const operationalManagers = new Set(teamMembers.map((member) => member.operationalManager?.name || 'N/A').filter(Boolean))
  const functionalAreas = new Set(teamMembers.map((member) => member.functionalArea).filter(Boolean))

  return {
    'Access Role': ['Admin', 'Manager', 'User'],
    Supervisor: Array.from(supervisors).sort(),
    'Operations Manager': Array.from(operationalManagers).sort(),
    'Functional Area': Array.from(functionalAreas).sort(),
    Practice: Array.from(practices).sort(),
  }
})

// Active Filter Count Selector
export const selectActiveFilterCount = createSelector(
  [selectTeamMemberFilters],
  (filters): number => {
    return (
      (filters.supervisor?.length || 0) +
      (filters.operationalManager?.length || 0) +
      (filters.practice?.length || 0) +
      (filters.functionalArea?.length || 0) +
      (filters.accessRole?.length || 0)
    )
  },
)

// Team Member Points Selector
export const selectTeamMemberPoints = (state: RootState) => state.teamMember.teamMemberPoints

// Loading Selectors
export const selectLoadingTeamMembers = (state: RootState) => state.teamMember.loadingTeamMembers
export const selectLoadingTeamMemberPoints = (state: RootState) =>
  state.teamMember.loadingTeamMemberPoints

// Error Selectors
export const selectErrorTeamMembers = (state: RootState) => state.teamMember.errorTeamMembers
export const selectErrorTeamMemberPoints = (state: RootState) => state.teamMember.errorTeamMemberPoints

// Sync Counts Selector
export const selectSyncCounts = (state: RootState) =>
  state.teamMember.syncCounts || {
    updatedCount: 0,
    addedCount: 0,
    terminatedCount: 0,
  }

// Last Update Info Selector
export const selectLastUpdateInfo = (state: RootState) => state.teamMember.lastUpdateInfo

